/**
 * LFC TACTICAL LAB - NEURAL CORE SYSTEM v4.0
 * Complete State Management, Event System, and Business Logic
 *
 * 5-phase flow: 01 Identity -> 02 Armory -> 03 Configuration -> 04 Manifest
 * -> 05 Transmission. Chromatic (color-matching) phase was removed - garment
 * color is chosen per-product in Armory/Configuration instead of matched
 * globally against extracted art colors.
 */

(function() {
  'use strict';

  // Initialize global namespace
  window.LFC = window.LFC || {};

  // Raw File objects live OUTSIDE window.LFC.state on purpose - state is
  // JSON-serialized to localStorage on every change, and a File object
  // can't survive that round-trip. Kept here so deploy() can do a real
  // multipart upload; never touched by storage.save()/load().
  window.LFC.rawFiles = window.LFC.rawFiles || { primary: null, additional: [] };

  // ═══════════════════════════════════════════════════════════════
  // CORE STATE STRUCTURE
  // ═══════════════════════════════════════════════════════════════

  const initialState = {
    currentPhase: 1,
    phaseUnlocked: {
      1: true,
      2: false,
      3: false,
      4: false,
      5: false
    },
    insigniaFiles: {
      primary: null,
      additional: []
    },
    // Team/contact info and due-date/shipping/notes are captured on the
    // real Shopify cart page instead (see sections/lfc-cart-order-details
    // .liquid), which writes/reads them directly as cart attributes - not
    // part of this builder's state, so they persist independently of
    // whether the director ever revisits the builder.
    // Each entry in selectedProducts carries its own `placement`, `selectedColor`
    // and `artSlotIndex` (set via phase03.setProductPlacement /
    // phase02.selectProductColor / phase03.setProductArtSlot) instead of one
    // choice shared across the whole bundle - a cap and a tee legitimately need
    // different print positions, colors and even different logos.
    selectedProducts: [],
    // Each roster entry is { id, name, number, pieces } where `pieces` is a map
    // keyed by product id: { [productId]: { optedIn, size } }. A player only
    // gets charged/cart-shipped for the pieces they opted into - not every
    // bundle item is bought by every player.
    roster: [],
    totalUnits: 0,
    missionCode: null,
    orderSummary: {
      subtotal: 0,
      setupFee: 15,
      total: 15
    },
    proofApproved: false,
    transmissionStatus: null
  };

  // Clone initial state
  window.LFC.state = JSON.parse(JSON.stringify(initialState));

  // Print-shop business rules - harvested from the earlier, unreferenced
  // lfc-validator-daemon.js prototype (its thresholds, wired into this
  // engine's actual validation gate for the first time). No minimum
  // order quantity - a bundle of any size is allowed.
  window.LFC.businessRules = {
    MIN_DPI_GRADE: 'GRADE_B', // handleFileUpload's analyzeDPI already grades uploads A/B/SYSTEM_FAILURE
    MAX_FILE_SIZE_MB: 25
  };

  // ═══════════════════════════════════════════════════════════════
  // SHARED UTILITIES
  // ═══════════════════════════════════════════════════════════════

  // Common apparel/print-shop color names -> approximate hex. Shopify
  // color options are free-text labels, not hex values, so this is only
  // used to rank "closest match" and render a fallback swatch when a
  // variant has no real per-color product photo.
  window.LFC.colorNames = {
    'black': '#000000', 'white': '#ffffff', 'navy': '#1e3a5f', 'red': '#c8102e',
    'royal': '#254aa5', 'maroon': '#5d0e26', 'cardinal': '#8c1d40',
    'forest green': '#1e4620', 'dark green': '#1e4620', 'kelly green': '#00843d',
    'athletic gold': '#b8860b', 'vegas gold': '#c5a253', 'gold': '#d4af37',
    'graphite': '#4a4a4a', 'silver': '#c0c0c0', 'grey': '#808080', 'gray': '#808080',
    'columbia blue': '#a4c8e1', 'brown': '#5c4033', 'orange': '#ff7300',
    'purple': '#5b2a86', 'pink': '#e75480', 'yellow': '#ffd700',
    'charcoal': '#36454f', 'tan': '#d2b48c', 'cream': '#f5f0dc',
    'burgundy': '#6d0f2b', 'teal': '#008080', 'turquoise': '#30d5c8',
    'mint': '#98d8c1', 'olive': '#6b6b1f', 'khaki': '#c3b091',
    'green': '#1e7d32', 'blue': '#1f4e8c', 'heather grey': '#9a9a9a',
    'heather gray': '#9a9a9a', 'storm': '#5b6470', 'steel': '#71797e',
    'natural': '#f0ead6', 'stone': '#928e85', 'ash': '#b2beb5',
    'sand': '#dcd0a8', 'ivory': '#fffff0', 'sport grey': '#9a9a9a',
    'sport gray': '#9a9a9a'
  };

  // Longest keys first, so a specific multi-word match ("heather grey")
  // wins over a generic single-word one ("grey") when both would match.
  const colorNameKeysByLength = Object.keys(window.LFC.colorNames).sort((a, b) => b.length - a.length);

  // Starting-point placement per garment category - harvested from the
  // earlier, unreferenced tactical-projections.js prototype's "anatomical
  // anchors" idea. These are only ever a SUGGESTED default shown the first
  // time a product is opened in Configuration's decorator; the moment a
  // director drags/adjusts it, their choice is what gets saved (see
  // phase03.setProductPlacement) - this never overrides a real placement.
  window.LFC.garmentAnchors = {
    headwear: { position: 'Front', x: 50, y: 40, scale: 70, rotation: 0 },
    tops: { position: 'Left Chest', x: 50, y: 45, scale: 100, rotation: 0 },
    bottoms: { position: 'Front', x: 50, y: 25, scale: 60, rotation: 0 },
    patches: { position: 'Front', x: 50, y: 50, scale: 160, rotation: 0 },
    default: { position: 'Front', x: 50, y: 50, scale: 100, rotation: 0 }
  };

  window.LFC.utils = {
    // Requests a properly-sized image from Shopify's CDN instead of the
    // full-resolution original (product photos here are typically
    // 1500x1500 - downloading that just to paint a 22px swatch circle or
    // a 180px card thumbnail is real, measurable page weight across
    // dozens of products). Shopify's CDN accepts a plain ?width= param
    // on any cdn.shopify.com file URL.
    resizeImage: function(url, width) {
      if (!url) return url;
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}width=${width}`;
    },

    // Resolves which uploaded art file a bundle product should use -
    // index 0 (default) is the primary upload; 1+ maps to that
    // "Additional Art" slot (the art library). Returns both the display
    // data (fileData, for previews/filenames) and the real File object
    // (rawFile, for the actual multipart cart upload) so a product can
    // genuinely use a different logo than the primary one (e.g. a sleeve
    // patch vs. the main chest logo) instead of every product being
    // forced onto the same single upload.
    getProductArtFile: function(product) {
      const idx = (product && product.artSlotIndex) || 0;
      if (idx === 0) {
        return {
          fileData: window.LFC.state.insigniaFiles.primary,
          rawFile: window.LFC.rawFiles.primary
        };
      }
      const i = idx - 1;
      return {
        fileData: window.LFC.state.insigniaFiles.additional[i] || null,
        rawFile: window.LFC.rawFiles.additional[i] || null
      };
    },

    getRawFileForSlot: function(slotIndex) {
      if (!slotIndex) return window.LFC.rawFiles.primary;
      return window.LFC.rawFiles.additional[slotIndex - 1] || null;
    },

    // Heuristic category detection from a product title - harvested from
    // tactical-projections.js, extended with a few more terms ("jersey",
    // "tee", "sweatshirt") that are core to this shop's own catalog.
    detectGarmentCategory: function(title) {
      const input = (title || '').toLowerCase();
      if (/\b(hat|cap|helmet|beanie)\b/.test(input)) return 'headwear';
      if (/\b(shirt|tee|jersey|hoodie|vest|jacket|sweatshirt)\b/.test(input)) return 'tops';
      if (/\b(pant|short|legging|jogger)\b/.test(input)) return 'bottoms';
      if (/\bpatch\b/.test(input)) return 'patches';
      return 'default';
    },

    suggestPlacement: function(productTitle) {
      const category = window.LFC.utils.detectGarmentCategory(productTitle);
      return window.LFC.garmentAnchors[category] || window.LFC.garmentAnchors.default;
    },

    // Dark garments read better with a lighter blend mode ("screen") so
    // the art doesn't just look like a black smear; light garments look
    // more natural with "multiply". Falls back to 'multiply' with no
    // garment color chosen yet.
    suggestBlendMode: function(garmentHex) {
      if (!garmentHex) return 'multiply';
      return window.LFC.utils.getLuminance(garmentHex) < 0.3 ? 'screen' : 'multiply';
    },

    // WCAG relative luminance - shared by anything that needs a quick
    // ratio for ranking, like sorting a product's color swatches by
    // contrast against its assigned art.
    getLuminance: function(hex) {
      const rgb = window.LFC.utils.hexToRgbArr(hex);
      if (!rgb) return 0;
      const [r, g, b] = rgb.map((val) => {
        val = val / 255;
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    },

    contrastRatio: function(hexA, hexB) {
      const lumA = window.LFC.utils.getLuminance(hexA);
      const lumB = window.LFC.utils.getLuminance(hexB);
      return lumA > lumB ? (lumA + 0.05) / (lumB + 0.05) : (lumB + 0.05) / (lumA + 0.05);
    },

    // Case-insensitive lookup against colorNames. Real Shopify color
    // labels vary a lot ("True Navy Heather", "Black/Red", "Storm Grey") -
    // tries an exact match first, then the first half of a compound
    // "X/Y" label, then falls back to finding any known color word
    // as a whole word anywhere inside the label.
    resolveColorHex: function(name) {
      if (!name) return null;
      const key = name.trim().toLowerCase();
      if (window.LFC.colorNames[key]) return window.LFC.colorNames[key];

      const firstPart = key.split('/')[0].trim();
      if (window.LFC.colorNames[firstPart]) return window.LFC.colorNames[firstPart];

      const match = colorNameKeysByLength.find((k) => new RegExp(`\\b${k}\\b`).test(key));
      return match ? window.LFC.colorNames[match] : null;
    },

    colorDistance: function(hexA, hexB) {
      const a = window.LFC.utils.hexToRgbArr(hexA);
      const b = window.LFC.utils.hexToRgbArr(hexB);
      if (!a || !b) return Infinity;
      return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);
    },

    hexToRgbArr: function(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
      return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
    },

    // Samples the uploaded art file on an offscreen canvas and buckets
    // pixels into quantized RGB groups to find its dominant colors -
    // downsampled to a small grid since exact color, not exact shape, is
    // all that's needed. Skips transparent pixels (real alpha, not just
    // "light") and near-white pixels (the blank canvas margin most print
    // files ship with) so those don't drown out the actual design colors.
    // Never throws - a canvas SecurityError or decode failure just means
    // no swatches render, which is a cosmetic miss, not a blocked upload.
    extractDominantColors: function(imageUrl, maxColors) {
      maxColors = maxColors || 5;
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          try {
            const size = 80;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, size, size);
            const data = ctx.getImageData(0, 0, size, size).data;

            const buckets = {};
            for (let i = 0; i < data.length; i += 4) {
              if (data[i + 3] < 128) continue;
              const r = data[i], g = data[i + 1], b = data[i + 2];
              if (r > 235 && g > 235 && b > 235) continue;
              const key = [Math.round(r / 24) * 24, Math.round(g / 24) * 24, Math.round(b / 24) * 24].join(',');
              buckets[key] = (buckets[key] || 0) + 1;
            }

            const hexes = Object.keys(buckets)
              .sort((a, b) => buckets[b] - buckets[a])
              .slice(0, maxColors)
              .map((key) => '#' + key.split(',').map((v) => Math.max(0, Math.min(255, Number(v))).toString(16).padStart(2, '0')).join(''));

            resolve(hexes);
          } catch (e) {
            resolve([]);
          }
        };
        img.onerror = () => resolve([]);
        img.src = imageUrl;
      });
    },

    // Whether any of a raw Armory product's color option values resolves
    // to a hex reasonably close to the given target hex - used to
    // highlight garment colors that match a clicked art-file swatch.
    productMatchesColor: function(product, targetHex) {
      if (!product || !targetHex) return false;
      const options = product.options || [];
      const colorOptionIndex = options.findIndex(o => /color|colour/i.test(o.name));
      if (colorOptionIndex === -1) return false;
      const values = options[colorOptionIndex].values || [];
      const threshold = 90; // empirically "close enough" out of a ~441 max Euclidean RGB distance
      return values.some((name) => {
        const hex = window.LFC.utils.resolveColorHex(name);
        return hex && window.LFC.utils.colorDistance(hex, targetHex) <= threshold;
      });
    },

    // Builds the list of real color choices for a product from its
    // Shopify option data - one entry per unique color value, using the
    // matching variant's real per-color product photo when Shopify
    // provides one (it does for most apparel), plus a best-effort hex
    // for a fallback swatch when it doesn't.
    extractColorOptions: function(product) {
      const options = product.options || [];
      const colorOptionIndex = options.findIndex(o => /color|colour/i.test(o.name));
      if (colorOptionIndex === -1) return [];

      const optionKey = 'option' + (colorOptionIndex + 1);
      const seen = {};
      const result = [];

      (product.variants || []).forEach(v => {
        const name = v[optionKey];
        if (!name || seen[name]) return;
        seen[name] = true;
        result.push({
          name: name,
          hex: window.LFC.utils.resolveColorHex(name),
          image: v.featured_image ? v.featured_image.src : null
        });
      });

      return result;
    },

    // Real size labels from the product's own Shopify option data (e.g.
    // "Youth M", "32", "One Size") instead of a hardcoded S/M/L/XL list -
    // a hardcoded list would never variant-match a product whose real
    // size option values differ, silently breaking cart resolution.
    extractSizeOptions: function(product) {
      const options = product.options || [];
      const sizeOptionIndex = options.findIndex(o => /size/i.test(o.name));
      if (sizeOptionIndex === -1) return [];

      const optionKey = 'option' + (sizeOptionIndex + 1);
      const seen = {};
      const result = [];

      (product.variants || []).forEach(v => {
        const name = v[optionKey];
        if (!name || seen[name]) return;
        seen[name] = true;
        result.push(name);
      });

      return result;
    },

    // Not every bundle item is sized apparel (caps, accessories are
    // usually one-size) - matching strictly by size title would silently
    // drop those from the roster's cart output. Prefer a variant matching
    // both the chosen color AND size, then size-only, then an available
    // variant, then any variant at all.
    resolveVariant: function(product, size, colorName) {
      const variants = product.variants || [];

      if (colorName) {
        const colorAndSize = variants.find(v => v.title.includes(colorName) && v.title.includes(size));
        if (colorAndSize) return colorAndSize;
        const colorOnly = variants.find(v => v.title.includes(colorName));
        if (colorOnly) return colorOnly;
      }

      const sizeMatch = variants.find(v => v.title.includes(size));
      if (sizeMatch) return sizeMatch;
      const firstAvailable = variants.find(v => v.available !== false);
      if (firstAvailable) return firstAvailable;
      return variants[0] || null;
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // EVENT SYSTEM (PUB/SUB)
  // ═══════════════════════════════════════════════════════════════

  const eventSubscribers = {};

  window.LFC.events = {
    subscribe: function(event, callback) {
      if (!eventSubscribers[event]) {
        eventSubscribers[event] = [];
      }
      eventSubscribers[event].push(callback);
    },

    publish: function(event, data) {
      if (!eventSubscribers[event]) return;
      eventSubscribers[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[LFC] Event callback error for ${event}:`, error);
        }
      });
    },

    unsubscribe: function(event, callback) {
      if (!eventSubscribers[event]) return;
      const index = eventSubscribers[event].indexOf(callback);
      if (index > -1) {
        eventSubscribers[event].splice(index, 1);
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // STORAGE SYSTEM
  // ═══════════════════════════════════════════════════════════════

  window.LFC.storage = {
    save: function() {
      try {
        localStorage.setItem('LFC_TACTICAL_LAB_STATE', JSON.stringify(window.LFC.state));
        return true;
      } catch (error) {
        console.error('[LFC] Storage save error:', error);
        return false;
      }
    },

    load: function() {
      try {
        const saved = localStorage.getItem('LFC_TACTICAL_LAB_STATE');
        if (saved) {
          window.LFC.state = JSON.parse(saved);
          window.LFC.events.publish('system:restored', { state: window.LFC.state });
          return true;
        }
        return false;
      } catch (error) {
        console.error('[LFC] Storage load error:', error);
        return false;
      }
    },

    clear: function() {
      try {
        localStorage.removeItem('LFC_TACTICAL_LAB_STATE');
        return true;
      } catch (error) {
        console.error('[LFC] Storage clear error:', error);
        return false;
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PHASE 01: IDENTITY
  // ═══════════════════════════════════════════════════════════════

  window.LFC.phase01 = {
    handleFileUpload: async function(file, slotType) {
      if (!file || !file.type.startsWith('image/')) {
        console.error('[LFC Phase 01] Invalid file type');
        return false;
      }

      try {
        const base64 = await this.convertToBase64(file);
        const dpiInfo = await this.analyzeDPI(file);
        const extractedColors = file.type === 'image/svg+xml'
          ? []
          : await window.LFC.utils.extractDominantColors(base64, 5);

        const fileData = {
          name: file.name,
          type: file.type,
          size: file.size,
          base64: base64,
          dpi: dpiInfo.dpi,
          dpiGrade: dpiInfo.grade,
          extractedColors: extractedColors,
          uploadedAt: new Date().toISOString()
        };

        if (slotType === 'primary') {
          window.LFC.state.insigniaFiles.primary = fileData;
          window.LFC.rawFiles.primary = file;
        } else {
          window.LFC.state.insigniaFiles.additional.push(fileData);
          window.LFC.rawFiles.additional.push(file);
        }

        window.LFC.storage.save();
        window.LFC.events.publish('file:uploaded', {
          slotType: slotType,
          fileData: fileData
        });

        this.checkPhaseCompletion();
        return true;

      } catch (error) {
        console.error('[LFC Phase 01] File upload error:', error);
        return false;
      }
    },

    // Thin wrapper so the Manifest phase's art-library "ADD" control has a
    // single, obviously-named method to call, without the caller needing
    // to know that a library file is really just an "additional" upload
    // slot under the hood.
    addLibraryFile: function(file) {
      return this.handleFileUpload(file, 'additional');
    },

    convertToBase64: function(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    analyzeDPI: function(file) {
      // SVGs are vector - infinitely scalable with no pixel grid, so a
      // pixel-dimension DPI estimate is meaningless for them (and often
      // wrong: an SVG with no explicit width/height renders at a tiny
      // default box, which would otherwise wrongly grade a print-ready
      // vector logo as SYSTEM_FAILURE). Vector art is print-ready by
      // definition, so it always grades as the best tier.
      if (file.type === 'image/svg+xml') {
        return Promise.resolve({ dpi: 300, grade: 'GRADE_A' });
      }

      return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
          const width = img.width;
          const height = img.height;
          const estimatedDPI = Math.max(width, height) > 2000 ? 300 :
                               Math.max(width, height) > 1000 ? 150 : 72;

          const grade = estimatedDPI >= 300 ? 'GRADE_A' :
                       estimatedDPI >= 150 ? 'GRADE_B' : 'SYSTEM_FAILURE';

          URL.revokeObjectURL(url);
          resolve({ dpi: estimatedDPI, grade: grade });
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          resolve({ dpi: 72, grade: 'SYSTEM_FAILURE' });
        };

        img.src = url;
      });
    },

    checkPhaseCompletion: function() {
      const hasRequired = window.LFC.state.insigniaFiles.primary !== null;

      if (hasRequired && !window.LFC.state.phaseUnlocked[2]) {
        window.LFC.state.phaseUnlocked[2] = true;
        window.LFC.storage.save();
        window.LFC.events.publish('phase:unlocked', { phase: 2 });
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PHASE 02: ARMORY
  // ═══════════════════════════════════════════════════════════════

  window.LFC.phase02 = {
    loadCollection: async function(collectionHandle, groupId) {
      try {
        const response = await fetch(`/collections/${collectionHandle}/products.json`);
        const data = await response.json();

        // NOTE: the storefront /products.json endpoint never returns
        // metafields (confirmed empirically), so a "lab_ready" metafield
        // filter here would always produce zero results regardless of
        // Admin settings. Every product in the configured collection is
        // eligible instead - curate via the collection itself.
        const products = data.products || [];

        window.LFC.events.publish('products:loaded', {
          products: products,
          groupId: groupId || collectionHandle
        });

        return products;

      } catch (error) {
        console.error('[LFC Phase 02] Collection load error:', error);
        window.LFC.events.publish('products:loaded', {
          products: [],
          groupId: groupId || collectionHandle
        });
        return [];
      }
    },

    toggleProduct: function(product) {
      const products = window.LFC.state.selectedProducts;
      const existingIndex = products.findIndex(p => p.id === product.id);
      let added;

      if (existingIndex > -1) {
        products.splice(existingIndex, 1);
        added = false;
        // A piece that's no longer in the bundle can't stay assignable -
        // drop its opt-in/size entry from every roster player.
        window.LFC.state.roster.forEach((player) => {
          delete player.pieces[product.id];
        });
      } else {
        const colorOptions = window.LFC.utils.extractColorOptions(product);
        const sizeOptions = window.LFC.utils.extractSizeOptions(product);
        products.push({
          id: product.id,
          title: product.title,
          price: product.variants[0].price,
          image: (product.images && product.images[0]) ? window.LFC.utils.resizeImage(product.images[0].src, 800) : null,
          colorOptions: colorOptions,
          selectedColor: colorOptions.length ? colorOptions[0].name : null,
          // A product with no real size option (accessories, one-size
          // caps) still needs something for the roster's size dropdown -
          // "One Size" both displays sensibly and matches Shopify's own
          // convention for unsized variants.
          sizeOptions: sizeOptions.length ? sizeOptions : ['One Size'],
          placement: null,
          artSlotIndex: 0, // 0 = primary art; 1+ = insigniaFiles.additional[index-1]
          variants: product.variants.map(v => ({
            id: v.id,
            title: v.title,
            price: v.price,
            available: v.available
          }))
        });
        added = true;
        // Every existing roster player gets a not-yet-opted-in entry for
        // the new piece, so Configuration's per-player checkbox rows
        // always have something to render/toggle regardless of add order.
        window.LFC.state.roster.forEach((player) => {
          if (!player.pieces[product.id]) {
            player.pieces[product.id] = { optedIn: false, size: null };
          }
        });
      }

      window.LFC.storage.save();
      window.LFC.events.publish('bundle:changed', { products: products, added: added, product: product });
      if (window.LFC.phase03) window.LFC.phase03.calculateTotals();
      this.checkPhaseCompletion();
    },

    selectProductColor: function(productId, colorName) {
      const product = window.LFC.state.selectedProducts.find(p => p.id === productId);
      if (!product) return;
      product.selectedColor = colorName;
      window.LFC.storage.save();
      window.LFC.events.publish('product:color-selected', { productId: productId, color: colorName });
      if (window.LFC.phase03) window.LFC.phase03.calculateTotals();
    },

    checkPhaseCompletion: function() {
      const hasProducts = window.LFC.state.selectedProducts.length > 0;

      if (hasProducts && !window.LFC.state.phaseUnlocked[3]) {
        window.LFC.state.phaseUnlocked[3] = true;
        window.LFC.storage.save();
        window.LFC.events.publish('phase:unlocked', { phase: 3 });
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PHASE 03: CONFIGURATION
  // ═══════════════════════════════════════════════════════════════

  window.LFC.phase03 = {
    setProductPlacement: function(productId, position, x, y, scale, rotation) {
      const product = window.LFC.state.selectedProducts.find(p => p.id === productId);
      if (!product) return;

      product.placement = {
        position: position,
        x: x || 50,
        y: y || 50,
        scale: scale || 100,
        rotation: rotation || 0
      };

      window.LFC.storage.save();
      window.LFC.events.publish('placement:updated', {
        productId: productId,
        placement: product.placement
      });
      this.checkPhaseCompletion();
    },

    setProductArtSlot: function(productId, slotIndex) {
      const product = window.LFC.state.selectedProducts.find(p => p.id === productId);
      if (!product) return;
      product.artSlotIndex = slotIndex;
      window.LFC.storage.save();
      window.LFC.events.publish('art-slot:changed', { productId: productId, slotIndex: slotIndex });
    },

    generatePlayerId: function() {
      return `PLR-${Date.now()}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
    },

    // No size argument here anymore - a player's size is chosen per piece
    // (see setPlayerPieceSize), since different pieces in the same bundle
    // can legitimately need different sizes for the same player.
    addPlayer: function(name, number) {
      if (!name) return null;

      const pieces = {};
      window.LFC.state.selectedProducts.forEach((product) => {
        pieces[product.id] = { optedIn: false, size: null };
      });

      const player = {
        id: this.generatePlayerId(),
        name: name,
        number: number || '',
        pieces: pieces
      };

      window.LFC.state.roster.push(player);
      this.calculateTotals();
      window.LFC.storage.save();
      window.LFC.events.publish('roster:updated', {
        roster: window.LFC.state.roster,
        added: player
      });
      this.checkPhaseCompletion();
      return player;
    },

    removePlayer: function(playerId) {
      window.LFC.state.roster = window.LFC.state.roster.filter(p => p.id !== playerId);
      this.calculateTotals();
      window.LFC.storage.save();
      window.LFC.events.publish('roster:updated', {
        roster: window.LFC.state.roster,
        removedId: playerId
      });
      this.checkPhaseCompletion();
    },

    // Opt a player in/out of one specific bundle piece - a roster of 15
    // doesn't mean 15 of every product; each player only gets (and pays
    // for) the pieces they've opted into.
    togglePlayerPiece: function(playerId, productId, optedIn) {
      const player = window.LFC.state.roster.find(p => p.id === playerId);
      if (!player) return;
      if (!player.pieces[productId]) player.pieces[productId] = { optedIn: false, size: null };
      player.pieces[productId].optedIn = !!optedIn;
      this.calculateTotals();
      window.LFC.storage.save();
      window.LFC.events.publish('roster:updated', { roster: window.LFC.state.roster, updatedId: playerId });
      this.checkPhaseCompletion();
    },

    setPlayerPieceSize: function(playerId, productId, size) {
      const player = window.LFC.state.roster.find(p => p.id === playerId);
      if (!player) return;
      if (!player.pieces[productId]) player.pieces[productId] = { optedIn: false, size: null };
      player.pieces[productId].size = size;
      this.calculateTotals();
      window.LFC.storage.save();
      window.LFC.events.publish('roster:updated', { roster: window.LFC.state.roster, updatedId: playerId });
    },

    // Sums only opted-in pieces, not every product for every player -
    // totalUnits is the real production unit count (piece count), not the
    // player count, which is tracked separately as roster.length.
    calculateTotals: function() {
      const roster = window.LFC.state.roster;
      const products = window.LFC.state.selectedProducts;

      let subtotal = 0;
      let pieceCount = 0;

      roster.forEach((player) => {
        Object.keys(player.pieces || {}).forEach((productId) => {
          const piece = player.pieces[productId];
          if (!piece || !piece.optedIn) return;

          const product = products.find(p => String(p.id) === String(productId));
          if (!product) return;

          const variant = window.LFC.utils.resolveVariant(product, piece.size, product.selectedColor);
          const price = variant ? parseFloat(variant.price) : parseFloat(product.price) || 0;
          subtotal += price;
          pieceCount += 1;
        });
      });

      const setupFee = 15;
      const total = subtotal + setupFee;

      window.LFC.state.totalUnits = pieceCount;
      window.LFC.state.orderSummary = {
        subtotal: subtotal,
        setupFee: setupFee,
        total: total
      };

      window.LFC.events.publish('totals:calculated', {
        totalUnits: pieceCount,
        subtotal: subtotal,
        total: total
      });
    },

    checkPhaseCompletion: function() {
      const products = window.LFC.state.selectedProducts;
      const hasRoster = window.LFC.state.roster.length > 0;
      const allConfigured = products.length > 0 && products.every(p => p.placement !== null);

      if (hasRoster && allConfigured && !window.LFC.state.phaseUnlocked[4]) {
        window.LFC.state.phaseUnlocked[4] = true;
        window.LFC.storage.save();
        window.LFC.events.publish('phase:unlocked', { phase: 4 });
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PHASE 04: MANIFEST
  // ═══════════════════════════════════════════════════════════════

  window.LFC.phase04 = {
    generateMissionCode: function() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      let code = 'LFC-';

      for (let i = 0; i < 6; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      window.LFC.state.missionCode = code;
      window.LFC.storage.save();
      window.LFC.events.publish('mission:code-generated', { code: code });

      if (!window.LFC.state.phaseUnlocked[5]) {
        window.LFC.state.phaseUnlocked[5] = true;
        window.LFC.storage.save();
        window.LFC.events.publish('phase:unlocked', { phase: 5 });
      }

      return code;
    },

    generateSummary: function() {
      return {
        missionCode: window.LFC.state.missionCode,
        totalUnits: window.LFC.state.totalUnits,
        roster: window.LFC.state.roster,
        selectedProducts: window.LFC.state.selectedProducts.map(p => ({ title: p.title, color: p.selectedColor })),
        orderSummary: window.LFC.state.orderSummary
      };
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PHASE 05: TRANSMISSION
  // ═══════════════════════════════════════════════════════════════

  window.LFC.phase05 = {
    setProofApproved: function(approved) {
      window.LFC.state.proofApproved = !!approved;
      window.LFC.storage.save();
      window.LFC.events.publish('proof:approved-changed', { approved: window.LFC.state.proofApproved });
    },

    // Team/contact info and due-date/shipping/notes are captured
    // separately on the real cart page (sections/lfc-cart-order-details
    // .liquid), not gated here - deploy just means "the bundle is
    // correctly configured," the same way adding a normal product to
    // cart doesn't require shipping info up front either.
    validatePreLaunch: function() {
      const primary = window.LFC.state.insigniaFiles.primary;

      const checks = {
        hasPrimaryFile: primary !== null,
        hasAcceptablePrintQuality: primary !== null && primary.dpiGrade !== 'SYSTEM_FAILURE',
        hasProducts: window.LFC.state.selectedProducts.length > 0,
        hasAllPlacements: window.LFC.state.selectedProducts.length > 0 && window.LFC.state.selectedProducts.every(p => p.placement !== null),
        hasRoster: window.LFC.state.roster.length > 0,
        hasMissionCode: window.LFC.state.missionCode !== null,
        hasProofApproved: window.LFC.state.proofApproved === true
      };

      const allValid = Object.values(checks).every(v => v === true);

      window.LFC.events.publish('validation:complete', {
        checks: checks,
        valid: allValid
      });

      return allValid;
    },

    deploy: async function() {
      if (!this.validatePreLaunch()) {
        console.error('[LFC Phase 05] Validation failed');
        return false;
      }

      try {
        window.LFC.state.transmissionStatus = 'TRANSMITTING';
        window.LFC.events.publish('transmission:started', {});

        const cartItems = this.buildCartPayload();

        // Group line items by which uploaded art file they actually need -
        // different bundle products can use different art (see
        // phase03.setProductArtSlot), so uploading only the primary file
        // once would silently attach the wrong artwork to any item using
        // an "Additional Art" slot instead. One real multipart upload per
        // distinct slot actually in use, its resulting hosted URL
        // propagated to every other item sharing that same slot.
        const groups = new Map();
        cartItems.forEach((item) => {
          const slot = item._artSlotIndex || 0;
          if (!groups.has(slot)) groups.set(slot, []);
          groups.get(slot).push(item);
          delete item._artSlotIndex;
        });

        for (const [slotIndex, groupItems] of groups) {
          const rawFile = window.LFC.utils.getRawFileForSlot(slotIndex);

          if (rawFile && groupItems.length) {
            const [firstItem, ...restItems] = groupItems;
            const uploadedUrl = await this.uploadArtWithItem(firstItem, rawFile);
            if (uploadedUrl) {
              restItems.forEach((item) => {
                item.properties['_lfc_art_primary'] = uploadedUrl;
              });
            }
            for (const item of restItems) {
              await this.addToCart(item);
            }
          } else {
            for (const item of groupItems) {
              await this.addToCart(item);
            }
          }
        }

        window.LFC.state.transmissionStatus = 'SUCCESS';
        window.LFC.storage.save();
        window.LFC.events.publish('transmission:complete', {});

        setTimeout(() => {
          window.location.href = '/cart';
        }, 1500);

        return true;

      } catch (error) {
        console.error('[LFC Phase 05] Deployment error:', error);
        window.LFC.state.transmissionStatus = 'FAILED';
        window.LFC.events.publish('transmission:failed', { error: error });
        return false;
      }
    },

    // Iterates roster players x only their OPTED-IN pieces (not every
    // product for every player) - one line item per player-piece.
    buildCartPayload: function() {
      const items = [];
      const roster = window.LFC.state.roster;
      const products = window.LFC.state.selectedProducts;

      roster.forEach((player) => {
        Object.keys(player.pieces || {}).forEach((productId) => {
          const piece = player.pieces[productId];
          if (!piece || !piece.optedIn) return;

          const product = products.find(p => String(p.id) === String(productId));
          if (!product) return;

          const variant = window.LFC.utils.resolveVariant(product, piece.size, product.selectedColor);
          if (!variant) return;

          const placement = product.placement || { position: 'Front', x: 50, y: 50, scale: 100, rotation: 0 };
          const art = window.LFC.utils.getProductArtFile(product);

          items.push({
            id: variant.id,
            quantity: 1,
            _artSlotIndex: product.artSlotIndex || 0,
            properties: {
              '_lfc_mission_code': window.LFC.state.missionCode,
              '_lfc_art_primary': art.fileData ? art.fileData.name : (window.LFC.state.insigniaFiles.primary ? window.LFC.state.insigniaFiles.primary.name : ''),
              '_lfc_product_color': product.selectedColor || 'Default',
              '_lfc_placement': placement.position,
              '_lfc_placement_x': placement.x,
              '_lfc_placement_y': placement.y,
              '_lfc_placement_scale': placement.scale,
              '_lfc_placement_rotation': placement.rotation,
              '_lfc_player_id': player.id,
              'Name': player.name,
              'Number': player.number
            }
          });
        });
      });

      return items;
    },

    // Shopify's /cart/add.js returns HTTP 200 with a real JSON body on
    // success, but ALSO returns a normal JSON body (just a different
    // shape, with a "description") on failure - e.g. HTTP 422 for a
    // sold-out or since-deleted variant. fetch() only rejects on true
    // network failure, never on an HTTP error status, so without this
    // check a sold-out variant during deploy would silently resolve as
    // if it succeeded - the director would see "DEPLOYED" and land on
    // an empty (or partially empty) cart, believing the order went
    // through when it didn't.
    addToCart: async function(item) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(item)
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.description || body.message || 'Cart add failed');
      }
      return body;
    },

    // Multipart upload (not JSON) is Shopify's native mechanism for a
    // property to become a real hosted file URL instead of plain text -
    // the same approach used elsewhere in this theme for art uploads.
    uploadArtWithItem: async function(item, file) {
      const formData = new FormData();
      formData.append('id', item.id);
      formData.append('quantity', item.quantity);
      Object.entries(item.properties).forEach(([key, value]) => {
        if (key === '_lfc_art_primary') return; // replaced by the real file below
        formData.append(`properties[${key}]`, value);
      });
      formData.append('properties[_lfc_art_primary]', file, file.name);

      const response = await fetch('/cart/add.js', { method: 'POST', body: formData });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.description || body.message || 'Cart add failed');
      }
      return body && body.properties ? body.properties['_lfc_art_primary'] : null;
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // NAVIGATION SYSTEM
  // ═══════════════════════════════════════════════════════════════

  window.LFC.navigation = {
    // Pure visibility switch, no gating/state side effects - used both by
    // goToPhase() for a real navigation action and once at page load to
    // render whichever phase the director was last on (from persisted
    // state), which isn't itself a "navigation" event worth re-publishing.
    // The header's own phase-tracker tiles carry the same [data-phase]
    // attribute as the phase panels for their own styling purposes, so
    // :not(.phase-node) is what tells the two apart.
    showPhase: function(phaseNumber) {
      document.querySelectorAll('[data-phase]:not(.phase-node)').forEach((el) => {
        el.classList.toggle('is-active-phase', String(el.dataset.phase) === String(phaseNumber));
      });
      window.scrollTo({ top: 0, behavior: 'instant' });
    },

    goToPhase: function(phaseNumber) {
      if (phaseNumber < 1 || phaseNumber > 5) {
        console.error('[LFC] Invalid phase number:', phaseNumber);
        return false;
      }

      if (!window.LFC.state.phaseUnlocked[phaseNumber]) {
        console.warn('[LFC] Phase not unlocked:', phaseNumber);
        return false;
      }

      window.LFC.state.currentPhase = phaseNumber;
      window.LFC.storage.save();
      window.LFC.events.publish('phase:changed', { phase: phaseNumber });
      this.showPhase(phaseNumber);

      return true;
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // RESET SYSTEM
  // ═══════════════════════════════════════════════════════════════

  window.LFC.reset = function() {
    window.LFC.storage.clear();
    window.LFC.state = JSON.parse(JSON.stringify(initialState));
    window.LFC.rawFiles = { primary: null, additional: [] };
    window.LFC.events.publish('system:reset', {});
    window.location.reload();
  };

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  function initialize() {
    console.log('[LFC NEURAL CORE] Initializing system...');

    const restored = window.LFC.storage.load();
    if (restored) {
      console.log('[LFC NEURAL CORE] State restored from storage');
    }

    window.LFC.navigation.showPhase(window.LFC.state.currentPhase);

    window.LFC.events.publish('system:ready', {
      state: window.LFC.state
    });

    console.log('[LFC NEURAL CORE] System online. All systems ready.');
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

})();
